/**
 * FeatureUpgradeModal
 *
 * Reusable upgrade-plan modal driven by FeatureAccessContext.
 * Renders automatically when `upgradeModal.open` is true - no need to
 * duplicate modal code across the app. Just call:
 *
 *   const { showUpgradeModal } = useFeatureAccess();
 *   showUpgradeModal('razorpay', 'Razorpay Integration');
 *
 * The modal shows the current plan, the required plan to unlock the
 * feature, and a CTA that redirects to the billing/choose-plan page.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureAccess } from '../../context/FeatureAccessContext';

export default function FeatureUpgradeModal() {
    const { upgradeModal, closeUpgradeModal, plan } = useFeatureAccess();
    const navigate = useNavigate();

    const { open, featureKey, featureLabel, requiredPlan } = upgradeModal;

    /* Lock body scroll while open */
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    /* Close on Escape */
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeUpgradeModal();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, closeUpgradeModal]);

    if (!open) return null;

    const displayName = featureLabel ?? featureKey ?? 'this feature';

    const handleUpgrade = () => {
        closeUpgradeModal();
        navigate('/billing/choose-plan');
    };

    return (


        
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Upgrade required"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) closeUpgradeModal();
            }}
        >
            <div className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-[var(--page)]">
                {/* Accent bar */}
                {/* <div className="h-1.5 w-full bg-[var(--accent)]" /> */}

                <div className="p-6 text-center">
                    {/* Icon */}
                    <div className="mb-4 mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] dark:bg-[color-mix(in_srgb,var(--accent)_20%,transparent)]">
                        <svg
                            className="h-6 w-6 text-[var(--accent)]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 10V3L4 14h7v7l9-11h-7z"
                            />
                        </svg>
                    </div>
                    {/* Title */}
                    <span className="mb-1 inline-block text-xs font-semibold tracking-widest text-[var(--accent)]">
                        Upgrade Required
                    </span>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                        Unlock {displayName}
                    </h2>

                    {/* Description */}
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        {requiredPlan ? (
                            <>
                                {/* Your current plan
                                <span className="mx-1 font-semibold text-gray-900 dark:text-white">
                                    {plan?.name ?? 'Free'}
                                </span>
                                doesn't include
                                <span className="mx-1 font-semibold text-gray-900 dark:text-white">
                                    {displayName}
                                </span>
                                .  */}
                                 Upgrade your plan to unlock this feature.
                                
                            </>
                        ) : (
                            <>
                                {/* Your current plan doesn't include
                                <span className="mx-1 font-semibold text-gray-900 dark:text-white">
                                    {displayName}
                                </span> */}
                                 Upgrade your plan to unlock this feature.
                            </>
                        )}
                    </p>

                    {/* Plan comparison (if required plan known) */}
                    {/* {requiredPlan && (
                        <div className="mt-4 grid grid-cols-2 gap-3">
                            <div className="rounded-lg border border-gray-200 p-3 text-center dark:border-gray-700">
                                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Current
                                </p>
                                <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                                    {plan?.name ?? 'Free'}
                                </p>
                                <p className="text-[11px] text-[var(--mute)] dark:text-gray-400">
                                    ${plan?.price ?? 0}/mo
                                </p>
                            </div>
                            <div className="rounded-lg border-2 border-[var(--accent)] p-3 text-center">
                                <p className="text-xs uppercase tracking-wide text-[var(--accent)]">
                                    Recommended
                                </p>
                                <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                                    {requiredPlan.name}
                                </p>
                                <p className="text-[11px] text-[var(--mute)] dark:text-gray-400">
                                    ${requiredPlan.price}/mo
                                </p>
                            </div>
                        </div>
                    )} */}

                    {/* Actions */}
                    <div className="mt-6 flex gap-3">
                        <button
                            type="button"
                            onClick={closeUpgradeModal}
                            className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
                        >
                            Maybe later
                        </button>
                        <button
                            type="button"
                            onClick={handleUpgrade}
                            className="flex-1 rounded-lg bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#36a6a0]"
                        >
                            Upgrade now
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
